import Button from 'components/button';
import Input from 'layouts/application-group/components/input';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import request from 'request';
import transcript from 'utils/transcript';
import styles from './create-modal.module.scss';
import { AxiosError } from 'axios';

const Modal = ({ onClose }: { onClose: () => void }) => {
	const [branch, setBranch] = React.useState('');
	const [currentApplicationWithSameFamilyID, setCurrentApplicationWithSameFamilyID] =
		React.useState('');
	const [loan_purpose, set_loan_purpose] = React.useState('');
	const [active_loans, set_active_loans] = React.useState('');
	const [city, set_city] = React.useState('');
	const [sum, setSum] = React.useState('');
	const [interestRate, setInterestRate] = React.useState('');
	const [duration, setDuration] = React.useState('');
	const [clientCount, setClientCount] = React.useState('');
	const [repeatedClient, setRepeatedClient] = React.useState('');
	const [loanDurationOfCurrentLoan, setLoanDurationOfCurrentLoan] = React.useState('');
	const [priorLoans, setPriorLoans] = useState('');
	const [numberOfPayments, setNumberOfPayments] = useState('');

	const [pending, setPending] = React.useState(false);
	const [validError, setValidError] = React.useState<
		{
			name:
				| 'clientCount'
				| 'repeatedClient'
				| 'loanDurationOfCurrentLoan'
				| 'activeLoans'
				| 'priorLoans'
				| 'numberOfPayments'
				| 'active_loans'
				| 'city'
				| 'sum'
				| 'interestRate'
				| 'duration'
				| 'branch'
				| 'loan_purpose'
				| 'familyId';
			value: string;
		}[]
	>([]);

	const navigate = useNavigate();

	useEffect(() => {
		if (repeatedClient === 'New' || active_loans === 'No') {
			setNumberOfPayments('0');
			setLoanDurationOfCurrentLoan('0');
			set_active_loans('No');
			setPriorLoans('1');
		} else if (
			(repeatedClient === 'Repeated' || active_loans === 'Yes') &&
			loanDurationOfCurrentLoan === '0'
		) {
			setLoanDurationOfCurrentLoan('1');
		}
	}, [repeatedClient, active_loans]);

	const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const validationErrors: typeof validError = [];

		if (branch.trim() === '') {
			validationErrors.push({ name: 'branch', value: 'Branch is required' });
		}
		if (loan_purpose.trim() === '') {
			validationErrors.push({ name: 'loan_purpose', value: 'Prohibited loan purpose is required' });
		}
		if (active_loans.trim() === '') {
			validationErrors.push({ name: 'active_loans', value: 'Active loans is required' });
		}
		if (city.trim() === '') {
			validationErrors.push({ name: 'city', value: 'Client residence is required' });
		}
		if (sum.trim() === '') {
			validationErrors.push({ name: 'sum', value: 'Amount is required' });
		}
		if (interestRate.trim() === '') {
			validationErrors.push({ name: 'interestRate', value: 'Interest rate is required' });
		}
		if (duration.trim() === '') {
			validationErrors.push({ name: 'duration', value: 'Duration is required' });
		}
		if (clientCount.trim() === '') {
			validationErrors.push({ name: 'clientCount', value: 'Number of clients is required' });
		}
		if (currentApplicationWithSameFamilyID.trim() === '') {
			validationErrors.push({
				name: 'familyId',
				value: 'Current application with same family ID is required',
			});
		}
		if (repeatedClient.trim() === '') {
			validationErrors.push({ name: 'repeatedClient', value: 'Repeated client is required' });
		}
		if (loanDurationOfCurrentLoan.trim() === '') {
			validationErrors.push({
				name: 'loanDurationOfCurrentLoan',
				value: 'Loan duration of current loan is required',
			});
		}
		if (priorLoans.trim() === '') {
			validationErrors.push({ name: 'priorLoans', value: 'Prior loans is required' });
		}
		if (numberOfPayments.trim() === '') {
			validationErrors.push({ name: 'numberOfPayments', value: 'Number of payments is required' });
		}

		if (validationErrors.length > 0) {
			return setValidError(validationErrors);
		}

		setPending(true);

		try {
			const res = await request.post('/applications/group', {
				loaners_amount: Number(clientCount),
			});
			const applicationsIds = res.data.applications_ids;
			const groupId = res.data.group_id;

			for (const applicationsId of applicationsIds) {
				await request.put(`/applications/group/${groupId}/${applicationsId}`, {
					amount: sum,
					interest_rate: interestRate,
					duration,
					active_loans_credit_cards: active_loans,
					repeated_client: transcript.clientTypeCode.getKeyFromValue(repeatedClient),
					number_of_payments: numberOfPayments,
					prior_loans: priorLoans,
					loan_duration_of_current_loan: loanDurationOfCurrentLoan,
					collected_data: {
						branch: transcript.branch.getKeyFromValue(branch),
					},
					stop_factors: {
						loan_purpose: loan_purpose === 'Yes',
						group_loan: true,
						current_application_with_same_family_id: currentApplicationWithSameFamilyID,
					},
					scoring_data: {
						city: transcript.city.getKeyFromValue(city),
					},
				});
			}

			navigate('/application-group/' + groupId + '/client/' + applicationsIds[0]);
		} catch (error: any) {
			if (error?.response?.status === 422) {
				toast.error('Validation error');
			} else {
				toast.error(error.message);
			}
		} finally {
			setPending(false);
		}
	};

	return (
		<div className={styles.modal} onMouseDown={onClose}>
			<form
				className={styles.modalWrap}
				onMouseDown={(event) => event.stopPropagation()}
				onSubmit={handleCreate}
			>
				<div className={styles.title}>New group application</div>
				<div className={styles.inputs}>
					<div className={styles.input}>
						<Input
							title="Branch"
							id="branch"
							value={branch}
							type="select"
							hasFilterText
							options={[
								'Bayader',
								'Zarqa',
								'Middle East',
								'Irbid',
								'Swaileh',
								'Zarqa Jadeedeh',
								'Rsaifeh',
								'Rsaifeh (Jabal Al Shamali)',
								'Hashmi',
								'Baqaa',
								'Marka',
								'Jabal Al Hussien',
								'Aqaba',
								'Irbid Aydoun',
								'Madaba',
								'Al Karak',
								'Ajloun',
								'Marj Al Hamam',
								'Abu Nseir',
								'Hai Nazal',
								'Al Salt',
								'Al Hurria',
								'Jabal Al Naser',
								'Tabarbour',
								'Jarash',
								'Mota Office',
								'Mafraq',
								'Dir Allah',
								'Head Office',
							]}
							onSelect={(option) => setBranch(option)}
						/>
						{validError.find((error) => error.name === 'branch')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'branch')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Prohibited loan purpose"
							id="loan_purpose"
							value={loan_purpose}
							type="select"
							options={['Yes', 'No']}
							infobox={
								<>
									• Facilities required for the purposes of manufacturing or selling weapons or
									related services.
									<br />
									<br />• Facilities required for gambling clubs or any purposes or activities
									related to them.
									<br />
									<br />• Facilities required for the manufacture of cigarettes, tobacco products
									and alcoholic beverages.
									<br />
									<br />• Facilities required for the production of prohibited medical drugs.
									<br />
									<br />• Facilities required to finance political offices.
								</>
							}
							onSelect={(option) => set_loan_purpose(option)}
						/>
						{validError.find((error) => error.name === 'loan_purpose')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'loan_purpose')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Active loans"
							value={active_loans}
							type="select"
							onSelect={(value) => set_active_loans(value)}
							options={['Yes', 'No']}
							disabled={repeatedClient === 'New'}
						/>
						{validError.find((error) => error.name === 'active_loans')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'active_loans')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Client residence"
							value={city}
							type="select"
							onSelect={(option) => set_city(option)}
							options={[
								'Al Balqa Governorate',
								'Capital Governorate',
								'Zarqa Governorate',
								'Irbid Governorate',
								'Aqaba Governorate',
								'Ajloun Governorate',
								'Madaba Governorate',
								'Karak Governorate',
								'Jerash Governorate',
								'Pillow',
								'Ramtha',
								'Mafraq Governorate',
							].sort()}
						/>
						{validError.find((error) => error.name === 'city')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'city')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Amount"
							type="integer"
							value={sum}
							onInput={(value) => setSum(value)}
							step={100}
							min={100}
							max={12000}
							unit="JOD"
						/>
						{validError.find((error) => error.name === 'sum')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'sum')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Interest rate (annual)"
							type="float"
							value={interestRate}
							onInput={(value) => setInterestRate(value)}
							step={1}
							min={12}
							max={37.6}
							unit="%"
						/>
						{validError.find((error) => error.name === 'interestRate')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'interestRate')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Duration"
							type="integer"
							value={duration}
							onInput={(value) => setDuration(value)}
							step={1}
							min={8}
							max={40}
							unit="mon."
						/>
						{validError.find((error) => error.name === 'duration')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'duration')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Number of clients"
							type="integer"
							value={clientCount}
							placeholder="max: 5 person"
							onInput={(value) => setClientCount(value)}
							step={1}
							min={2}
							max={5}
						/>
						{validError.find((error) => error.name === 'clientCount')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'clientCount')?.value}
							</div>
						)}
					</div>

					<div className={styles.input}>
						<Input
							title="Current application with same family ID"
							id="family_id"
							value={currentApplicationWithSameFamilyID}
							type="select"
							options={['Yes', 'No']}
							onSelect={(option) => setCurrentApplicationWithSameFamilyID(option)}
						/>
						{validError.find((error) => error.name === 'familyId')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'familyId')?.value}
							</div>
						)}
					</div>

					<div className={styles.input}>
						<Input
							title="Repeated client?"
							id="repeated_client"
							value={repeatedClient}
							type="select"
							options={['Repeated', 'New']}
							onSelect={(option) => setRepeatedClient(option)}
						/>

						{validError.find((error) => error.name === 'repeatedClient')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'repeatedClient')?.value}
							</div>
						)}
					</div>

					<div className={styles.input}>
						<Input
							title="Loan duration of current loan"
							value={loanDurationOfCurrentLoan}
							step={1}
							min={1}
							max={1000}
							type="integer"
							onInput={(value) => setLoanDurationOfCurrentLoan(value)}
							disabled={repeatedClient === 'New' || active_loans === 'No'}
						/>
						{validError.find((error) => error.name === 'loanDurationOfCurrentLoan')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'loanDurationOfCurrentLoan')?.value}
							</div>
						)}
					</div>

					<div className={styles.input}>
						<Input
							title="Prior loans"
							value={priorLoans}
							step={1}
							min={0}
							max={18}
							type="integer"
							onInput={(value) => setPriorLoans(value)}
							disabled={repeatedClient === 'New'}
						/>
						{validError.find((error) => error.name === 'priorLoans')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'priorLoans')?.value}
							</div>
						)}
					</div>

					<div className={styles.input}>
						<Input
							title="Number of payments"
							value={numberOfPayments}
							step={1}
							min={0}
							max={10000}
							type="integer"
							onInput={(value) => setNumberOfPayments(value)}
							disabled={repeatedClient === 'New' || active_loans === 'No'}
						/>
						{validError.find((error) => error.name === 'numberOfPayments')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'numberOfPayments')?.value}
							</div>
						)}
					</div>
				</div>

				<div className={styles.buttonGroup}>
					<Button color="orange" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" color="green" pending={pending}>
						Create
					</Button>
				</div>
			</form>
		</div>
	);
};

export default Modal;
